import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { TaxInvoice } from '../../../../shared/components/TaxInvoice';
import type { TaxInvoiceDoc } from '../../../../shared/contracts/tax-invoice.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useClaimBill } from './useClaimBill.js';
import { loadBillDocument } from './billDocument.js';

/** The vendor's bill as the back office sees it — same document the vendor prints. */
export function ClaimBillPage(): ReactNode {
  const { claimId } = useParams<{ claimId: string }>();
  const { bill, error: claimError } = useClaimBill(`/v1/billing/claims/${claimId}`);
  const { token } = useAuth();
  const { t } = useTranslation();
  const [doc, setDoc] = useState<TaxInvoiceDoc | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    loadBillDocument(claimId as string, token)
      .then(r => {
        if (alive) setDoc(r);
      })
      .catch(e => {
        if (alive) setDocError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [claimId, token]);

  // The heading names the bill by its job, dealer and date even when the
  // document cannot be built, and is left off the printed bill.
  const heading = (
    <h1 className="no-print" style={{ marginBottom: 12 }}>
      {t('billing.bill.title', { bill })}
    </h1>
  );
  const error = docError ?? claimError;

  if (error)
    return (
      <main aria-label="claim-bill">
        {heading}
        <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>
      </main>
    );
  if (!doc) return <main aria-label="claim-bill">{heading}<p>{t('common.loading')}</p></main>;

  return (
    <main aria-label="claim-bill">
      {heading}
      <div className="no-print" style={{ marginBottom: 12 }}>
        <button
          type="button"
          data-testid="print-bill"
          onClick={() => window.print()}
          style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid var(--color-border-default, #d0d7de)', cursor: 'pointer', fontWeight: 600 }}
        >
          {t('billing.bill.print')}
        </button>
      </div>
      <TaxInvoice doc={doc} />
    </main>
  );
}
